import { isDefAndNotNull } from '@utils/def';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import {
  Sizes as AutoCompleteSizes,
  OptionType,
} from '../../../../../AutoComplete';
import Input from '../../../../../Input';
import { MeasuringUnit } from '../../../../enums';
import {
  MEASURING_UNIT_LABEL,
  convertMeasuringUnit,
} from '../../../../measuringUnits';

import { StyledUnitLabel, StyledUnitPicker } from './styles';
import { Props, TypedValueDraft } from './types';

const MeasuringUnitInput: FC<Props> = ({
  measuringUnit,
  onChange,
  onBlur,
  value,
  ...rest
}) => {
  const [unit, setUnit] = useState(measuringUnit.originalUnit);
  const [draft, setDraft] = useState<TypedValueDraft | null>(null);
  const currentValue = isDefAndNotNull(value) ? String(value) : '';

  useEffect(() => {
    setUnit(measuringUnit.originalUnit);
    setDraft(null);
  }, [measuringUnit.originalUnit]);

  const displayValue = useMemo(() => {
    if (draft !== null && draft.forValue === currentValue) return draft.text;
    if (!isDefAndNotNull(value) || value === '') return value ?? '';

    return convertMeasuringUnit(
      Number(value),
      measuringUnit.originalUnit,
      unit,
    );
  }, [currentValue, draft, measuringUnit.originalUnit, unit, value]);

  const handleChange = useCallback(
    (nextValue: string) => {
      const isIncomplete =
        nextValue === '-' || /\.$/.test(nextValue) || nextValue === '';

      if (isIncomplete) {
        setDraft({
          text: nextValue,
          forValue: nextValue === '' ? '' : currentValue,
        });
        if (nextValue === '') onChange?.('');
        return;
      }

      const numeric = Number(nextValue);

      if (Number.isNaN(numeric)) {
        setDraft(null);
        onChange?.(nextValue);
        return;
      }
      const nextOriginalValue = String(
        convertMeasuringUnit(numeric, unit, measuringUnit.originalUnit),
      );
      const isTypedAsIs = String(numeric) === nextValue;

      setDraft(
        isTypedAsIs ? null : { text: nextValue, forValue: nextOriginalValue },
      );
      onChange?.(nextOriginalValue);
    },
    [currentValue, measuringUnit.originalUnit, onChange, unit],
  );

  const handleBlur = useCallback(
    (blurredValue: string) => {
      setDraft(null);
      onBlur?.(blurredValue);
    },
    [onBlur],
  );

  const unitOptions: OptionType[] = useMemo(
    () =>
      (measuringUnit.availableUnits ?? []).map((option) => ({
        value: option,
        label: MEASURING_UNIT_LABEL[option],
      })),
    [measuringUnit.availableUnits],
  );

  const handleUnitChange = useCallback(
    (option: OptionType | OptionType[] | null) => {
      if (!option || Array.isArray(option)) return;

      setDraft(null);
      setUnit(option.value as MeasuringUnit);
    },
    [],
  );

  return (
    <Input
      {...rest}
      value={displayValue}
      onChange={handleChange}
      onBlur={handleBlur}
      postfix={
        unitOptions.length > 1 ? (
          <StyledUnitPicker
            name={`${rest.name}-unit`}
            options={unitOptions}
            value={unit}
            onChange={handleUnitChange}
            disableClearable
            size={AutoCompleteSizes.small}
          />
        ) : (
          <StyledUnitLabel>
            {MEASURING_UNIT_LABEL[measuringUnit.originalUnit]}
          </StyledUnitLabel>
        )
      }
    />
  );
};

export default MeasuringUnitInput;
